import React, { Fragment } from "react";
import { useSelector } from "react-redux";
import moment from "moment";

import { FIELD_TITLES } from "./constants";
import { hasNonEmptyValues } from "./utils";
import "./serviceInformation.scss";

const RiskBox = ({ title, value, explanation }) => (
  <div className="risk-box">
    <label className="form-element-label">{title}</label>
    <div className="form-element-data-text">
      <p>{value}</p>
    </div>
    {explanation && (
      <>
        <label className="form-element-label">
          Обоснование высокой степени риска
        </label>
        <div className="form-element-data date">
          <div className="form-element-data-text">
            <p>{explanation}</p>
          </div>
        </div>
      </>
    )}
  </div>
);

const GridItem = ({ label, value, dateFormat, className }) => (
  <div className={className}>
    <label className="form-element-label">{label}</label>
    <div className="form-element-data">
      <div className="form-element-data-text">
        <p>{dateFormat ? moment(value).format(dateFormat) : value}</p>
      </div>
    </div>
  </div>
);

const ServiceInfoView = ({ serviceInfo }) => {
  const { accessFeatures } = useSelector(({user}) => user);
  const aimAndRelationshipFeature = accessFeatures['client.aim_and_relationship']
  const {
    riskGeographic,
    riskProfile,
    riskOperationType,
    riskFinal,
    riskGeographicExplanation,
    riskProfileExplanation,
    riskOperationTypeExplanation,
    riskFinalExplanation,
    furtherActions = [],
    blockTransactions = [],
    suspiciousTransactions = [],
    otherInformation,
    lackIdentifierDataReason,
  } = { ...serviceInfo };

  const getLabelOrValue = (item) => item?.label || item;

  const hasBlockData = (data) => {
    if (!Array.isArray(data) || !data.length) return false;
    return data.some(hasNonEmptyValues);
  };

  const riskFields = [
    {
      title: "Итоговая степень риска",
      value: riskFinal,
      explanation: riskFinalExplanation,
      isShow: true,
    },
    {
      title: "Риск по географическому региону",
      value: riskGeographic,
      explanation: riskGeographicExplanation,
    },
    {
      title: "Риск по профилю клиента",
      value: riskProfile,
      explanation: riskProfileExplanation,
    },
    {
      title: "Риск по видам операций",
      value: riskOperationType,
      explanation: riskOperationTypeExplanation,
    },
  ];

  const renderRiskFields = (riskFields) => {
    return riskFields
      .filter(({ explanation, isShow }) => explanation || isShow)
      .map(({ title, value, explanation }) => (
        <RiskBox
          key={title}
          title={title}
          value={value}
          explanation={explanation}
        />
      ));
  };

  return (
    <div className="form-row serviceInformationAdd">
      <div className="form-row-item">
        <div className="serviceInformationAdd-risk-container">
          {renderRiskFields(riskFields)}
        </div>

        <div className="serviceInformationAdd-grid-container grid-cols-2 m-t-spacer">
          {hasBlockData(furtherActions) && (
            <div>
              <label className="form-element-label">
                Решение ОДЛ о дальнейших действиях в отношении клиента
              </label>
              {furtherActions.filter(hasNonEmptyValues).map(
                ({ fullName, dateDecision, dateCompletion, decision }, index) =>
                  hasBlockData(furtherActions) && (
                    <div
                      key={index}
                      className="form-element-data entry-wrapper"
                    >
                      <div className="serviceInformationAdd-grid-container grid-cols-3">
                        {fullName && (
                          <GridItem
                            label="ФИО"
                            value={getLabelOrValue(fullName)}
                          />
                        )}
                        {dateDecision && (
                          <GridItem
                            label="Дата решения"
                            value={dateDecision}
                            dateFormat="DD.MM.yyyy"
                          />
                        )}
                        {dateCompletion && (
                          <GridItem
                            label="Срок выполнения"
                            value={dateCompletion}
                            dateFormat="DD.MM.yyyy"
                          />
                        )}
                        {decision && (
                          <GridItem
                            label="Решение"
                            value={decision}
                            className="col-span-2"
                          />
                        )}
                      </div>
                    </div>
                  )
              )}
            </div>
          )}

          {hasBlockData(blockTransactions) && (
            <div>
              <label className="form-element-label">
                Решение ОДЛ о замораживании средств и (или) блокировании
                финансовой операции в отношении лиц, причастных к
                террористической деятельности
              </label>
              {blockTransactions
                .filter(hasNonEmptyValues)
                .map(({ fullName, dateDecision, decision }, index) => (
                  <div
                    key={index}
                    className="serviceInformationAdd-grid-container grid-cols-2 entry-wrapper"
                  >
                    {fullName && (
                      <GridItem label="ФИО" value={getLabelOrValue(fullName)} />
                    )}
                    {dateDecision && (
                      <GridItem
                        label="Дата решения"
                        value={dateDecision}
                        dateFormat="DD.MM.yyyy"
                      />
                    )}
                    {decision && (
                      <GridItem
                        label="Решение"
                        value={decision}
                        className="col-span-2"
                      />
                    )}
                  </div>
                ))}
            </div>
          )}

          {hasBlockData(suspiciousTransactions) && (
            <div className="col-span-2">
              <div className="form-element">
                <label className="form-element-label">
                  Сведения о подозрительных финансовых операциях клиента
                </label>
                {suspiciousTransactions
                  .filter(hasNonEmptyValues)
                  .map(
                    ({codeSuspicion, referralDate, specialForm, financeSuspiciousOperations }, index) => (
                      <div
                        key={index}
                        className="serviceInformationAdd-grid-container grid-cols-5"
                      >
                        {codeSuspicion && (
                          <GridItem
                            label="Код признака подозрительности"
                            value={codeSuspicion}
                          />
                        )}
                        {referralDate && (
                          <GridItem
                            label="Дата направления"
                            value={referralDate}
                            dateFormat="DD.MM.yyyy"
                          />
                        )}
                        {specialForm && (
                          <GridItem
                            label="Номер специального формуляра"
                            value={specialForm}
                          />
                        )}
                        <div className="serviceInformationAdd-grid-container grid-cols-2">
                          {financeSuspiciousOperations?.map((field, index) => (
                            <Fragment key={index}>
                              {field.dealNumber && (
                                <GridItem
                                  label="Финансовая операция"
                                  value={getLabelOrValue(field.dealNumber)}
                                />
                              )}
                              {field.dateTransaction && (
                                <GridItem
                                  label="Дата финансовой операции"
                                  value={field.dateTransaction}
                                  dateFormat="DD.MM.yyyy"
                                />
                              )}
                            </Fragment>
                          ))}
                        </div>
                      </div>
                    )
                  )}
              </div>
            </div>
          )}
        </div>
          {lackIdentifierDataReason && 
            <div className="form-element-data entry-wrapper">
              <label className="form-element-label">
              {FIELD_TITLES.lackIdentifierDataReason}
              </label>
              <div className="form-element-data form-element-data-text__full">
                <p>{lackIdentifierDataReason}</p>
              </div>
            </div>}
          {!aimAndRelationshipFeature && otherInformation &&
            <div className="form-element-data entry-wrapper">
              <label className="form-element-label">
                {FIELD_TITLES.otherInformation}
              </label>
              <div className="form-element-data form-element-data-text__full">
                <p>{otherInformation}</p>
              </div>
            </div>}
      </div>
    </div>
  );
};

export default ServiceInfoView;
